import gsap from 'gsap'
import { support, split } from '../utils'
import { CustomEase } from 'gsap/CustomEase'

gsap.registerPlugin(CustomEase)

CustomEase.create('customEase', '0.215, 0.61, 0.355, 1')

class Gallery {
  constructor() {
    this.bindFns()

    this.DOM = this.getDOM()

    // sets the base height for all descriptions --
    const base = this.setBaseDescription(this.DOM.descriptions)
    base.classList.add('our-hotels__description--base')

    this.carousels = this.DOM.carousels.map(this.getCarouselData)

    this.descriptions = this.DOM.descriptions.map(this.splitDescriptions)

    this.state = {
      activeGallery: 0,
      animating: false,
      interval: null,
      timing: 5000,
    }

    this.DOM.hotelButtons.forEach((button) => {
      button.addEventListener('mousedown', this.setActiveGallery)
    })

    this.DOM.prevArrow.addEventListener('click', this.clickSlide)
    this.DOM.nextArrow.addEventListener('click', this.clickSlide)

    this.state.interval = setInterval(this.nextSlide, this.state.timing)

    this.activateCurrentCarousel(this.state.activeGallery)

    window.addEventListener('resize', this.resize)
  }

  bindFns() {
    ;[
      'setActiveGallery',
      'getCarouselData',
      'clickSlide',
      'nextSlide',
      'resize',
      'toggleText',
    ].forEach((fn) => (this[fn] = this[fn].bind(this)))
  }

  getDOM() {
    return {
      hotelButtons: document.querySelectorAll('.our-hotels__button'),
      carousels: Array.from(
        document.querySelectorAll('.our-hotels__images-container')
      ),
      descriptions: Array.from(
        document.querySelectorAll('.our-hotels__description')
      ),
      nextArrow: document.getElementById('next-arrow'),
      prevArrow: document.getElementById('prev-arrow'),
    }
  }

  setBaseDescription(descriptions) {
    return descriptions.reduce((previousDesc, currentDesc) => {
      const previousHeight = previousDesc.getBoundingClientRect
        ? previousDesc.getBoundingClientRect().height
        : 0
      const currentHeight = currentDesc.getBoundingClientRect().height
      return previousHeight > currentHeight ? previousDesc : currentDesc
    })
  }

  getCarouselData(element) {
    const images = element.querySelectorAll('.our-hotels__image')

    return {
      element: element,
      images: images,
      length: images.length,
      activeImage: 0,
    }
  }

  splitCaptions(caption) {
    return split({
      contentType: 'headline',
      textElement: caption,
    })
  }

  splitDescriptions(description) {
    return split({
      contentType: 'paragraph',
      textElement: description,
    })
  }

  setActiveGallery({ currentTarget }) {
    if (
      this.state.activeGallery === parseInt(currentTarget.dataset.index) ||
      this.state.animating
    )
      return
    this.state.activeGallery = parseInt(currentTarget.dataset.index)
    this.activateCurrentCarousel()
  }

  activateCurrentCarousel() {
    this.resetCarousel()
    this.activateDescription()
    this.activateButton()

    // wait for text to animate out
    setTimeout(
      () => {
        this.activateImages()
      },
      support.touch ? 0 : 500
    )
  }

  activateDescription() {
    this.DOM.descriptions.forEach(this.toggleText)
  }

  activateButton() {
    this.DOM.hotelButtons.forEach((button) =>
      button.classList.remove('is-active')
    )
    this.DOM.hotelButtons[this.state.activeGallery].classList.add('is-active')
  }

  activateImages() {
    this.DOM.carousels.forEach((carousel) =>
      carousel.classList.remove('is-active')
    )
    this.DOM.carousels[this.state.activeGallery].classList.add('is-active')
  }

  resetCarousel() {
    clearInterval(this.state.interval)
    this.state.interval = setInterval(this.nextSlide, this.state.timing)
  }

  toggleText(description) {
    this.state.animating = true

    const lines = description.querySelectorAll('.line-container')

    gsap.to(lines, {
      y: '100%',
      opacity: 0,
      duration: 0.5,
      force3D: true,
      ease: 'Quint.easeIn',
      onComplete: () => {
        description.classList.remove('is-active')
        this.DOM.descriptions[this.state.activeGallery].classList.add(
          'is-active'
        )
        const currentLines = this.DOM.descriptions[
          this.state.activeGallery
        ].querySelectorAll('.line-container')
        gsap.fromTo(
          currentLines,
          { y: '100%', opacity: 0.5 },
          {
            y: '0%',
            duration: 0.65,
            opacity: 1,
            ease: 'Expo.easeOut',
            stagger: 0.02,
            force3D: true,
            onComplete: () => (this.state.animating = false),
          }
        )
      },
    })
  }

  clickSlide({ currentTarget }) {
    const target = currentTarget

    this.resetCarousel()

    target.id === 'next-arrow' ? this.nextSlide() : this.prevSlide()
  }

  nextSlide() {
    const currentCarousel = this.carousels[this.state.activeGallery]

    if (currentCarousel.activeImage >= currentCarousel.length - 1) {
      currentCarousel.activeImage = 0
      this.nextGallery()
    } else {
      currentCarousel.activeImage++
    }

    currentCarousel.images.forEach((image) =>
      image.classList.remove('is-active')
    )
    currentCarousel.images[currentCarousel.activeImage].classList.add(
      'is-active'
    )
  }

  nextGallery() {
    this.state.activeGallery =
      (this.state.activeGallery + 1) % this.DOM.hotelButtons.length
    this.activateCurrentCarousel()
  }

  prevSlide() {
    const currentCarousel = this.carousels[this.state.activeGallery]

    currentCarousel.activeImage <= 0
      ? (currentCarousel.activeImage = currentCarousel.length - 1)
      : currentCarousel.activeImage--

    currentCarousel.images.forEach((image) =>
      image.classList.remove('is-active')
    )
    currentCarousel.images[currentCarousel.activeImage].classList.add(
      'is-active'
    )
  }

  resize() {
    this.descriptions.forEach((description) => {
      description.revert()
    })

    this.descriptions = this.DOM.descriptions.map(this.splitDescriptions)
  }
}

export default Gallery
